/*
 * assets/product.js — Boostly product page behaviour.
 * Replaces: theme.Product + slate.Variants (theme.js), jquery.zoom, slick thumbnails, variantImages map,
 * ajaxify-cart.liquid, productPage.js (visitor counter), readmore.min.js, relate_pop() / upsell #myModal,
 * the sticky add-to-cart JS and the swatch → .single-option-selector sync of layout/theme.liquid.
 *   <boostly-gallery> <boostly-variant-picker> <boostly-product-form> <boostly-sticky-atc>
 *   <boostly-visitor-counter> <boostly-read-more> <boostly-quantity-upsell>
 */
(function () {
  'use strict';
  const define = (n, c) => { if (!customElements.get(n)) customElements.define(n, c); };
  const T = () => window.Theme;
  const str = (k, fb) => (T() && T().strings[k]) || fb;

  /* ================================================================== gallery */
  class BoostlyGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = Array.from(this.querySelectorAll('.gallery__slide'));
      this.thumbs = Array.from(this.querySelectorAll('.gallery__thumb'));
      this.thumbList = this.querySelector('[data-thumbs]');
      try { this.mediaVariants = JSON.parse((this.querySelector('[data-media-variants]') || {}).textContent || '[]'); } catch (e) { this.mediaVariants = []; }
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.thumbs.forEach((t) => t.addEventListener('click', () => {
        const id = t.dataset.mediaId;
        this.show(id, { fromThumb: true });
        const mv = this.mediaVariants.find((m) => String(m.media) === String(id));
        if (mv) this.dispatchEvent(new CustomEvent('gallery:select-variant', { bubbles: true, detail: { variantId: mv.variant } }));
      }));
      const prev = this.querySelector('[data-gallery-prev]'), next = this.querySelector('[data-gallery-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
      if (next) next.addEventListener('click', () => this.go(this.index + 1));
      const tp = this.querySelector('[data-thumbs-prev]'), tn = this.querySelector('[data-thumbs-next]');
      if (tp && this.thumbList) tp.addEventListener('click', () => this.thumbList.scrollBy({ left: -this.thumbList.clientWidth * .8, top: -this.thumbList.clientHeight * .8, behavior: 'smooth' }));
      if (tn && this.thumbList) tn.addEventListener('click', () => this.thumbList.scrollBy({ left: this.thumbList.clientWidth * .8, top: this.thumbList.clientHeight * .8, behavior: 'smooth' }));
      // swipe on mobile
      let x0 = null;
      this.track.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      this.track.addEventListener('touchend', (e) => {
        if (x0 === null) return; const dx = e.changedTouches[0].clientX - x0; x0 = null;
        if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1));
      });
      this.addEventListener('keydown', (e) => { if (e.key === 'ArrowLeft') this.go(this.index - 1); if (e.key === 'ArrowRight') this.go(this.index + 1); });
      if (this.dataset.zoom === 'true') this.initZoom();
    }
    go(i) {
      if (!this.slides.length) return;
      i = (i + this.slides.length) % this.slides.length;
      this.show(this.slides[i].dataset.mediaId);
    }
    show(mediaId) {
      const i = this.slides.findIndex((s) => String(s.dataset.mediaId) === String(mediaId));
      if (i < 0) return;
      this.index = i;
      this.slides.forEach((s, n) => { const on = n === i; s.classList.toggle('is-active', on); s.setAttribute('aria-hidden', !on); if (!on) { const v = s.querySelector('video'); if (v) v.pause(); } });
      this.thumbs.forEach((t) => { const on = String(t.dataset.mediaId) === String(mediaId); t.classList.toggle('is-active', on); if (on) { t.setAttribute('aria-current', 'true'); t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' }); } else t.removeAttribute('aria-current'); });
    }
    initZoom() {
      if (!window.matchMedia('(hover: hover) and (min-width: 750px)').matches) return;
      this.querySelectorAll('.gallery__image-wrap.is-zoomable').forEach((wrap) => {
        let zoomImg = null;
        wrap.addEventListener('mouseenter', () => {
          if (!zoomImg) { zoomImg = new Image(); zoomImg.src = wrap.dataset.zoomSrc; zoomImg.className = 'gallery__zoom'; zoomImg.alt = ''; wrap.appendChild(zoomImg); }
          wrap.classList.add('is-zooming');
        });
        wrap.addEventListener('mousemove', (e) => {
          if (!zoomImg) return;
          const r = wrap.getBoundingClientRect();
          zoomImg.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
        });
        wrap.addEventListener('mouseleave', () => wrap.classList.remove('is-zooming'));
      });
    }
  }
  define('boostly-gallery', BoostlyGallery);

  /* ============================================================ variant picker */
  class BoostlyVariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.section = document.querySelector(`[data-section-id="${this.sectionId}"].product`) || this.closest('.product') || document;
      try { this.variants = JSON.parse(this.querySelector('[data-variants]').textContent); } catch (e) { this.variants = []; }
      this.unselected = this.hasAttribute('data-unselected');
      this.addEventListener('change', () => this.onChange());
      this.section.addEventListener('gallery:select-variant', (e) => this.selectVariant(e.detail.variantId));
      this.updateAvailability();
    }
    getOptions() {
      const selects = Array.from(this.querySelectorAll('select[data-option-position]'));
      if (selects.length) return selects.map((s) => s.value);
      return Array.from(this.querySelectorAll('fieldset.swatch')).map((f) => { const c = f.querySelector('input:checked'); return c ? c.value : ''; });
    }
    selectVariant(id) {
      const v = this.variants.find((x) => String(x.id) === String(id)); if (!v) return;
      v.options.forEach((val, i) => {
        const sel = this.querySelector(`select[data-option-position="${i + 1}"]`);
        if (sel) sel.value = val;
        const radio = Array.from(this.querySelectorAll(`input[data-option-position="${i + 1}"]`)).find((r) => r.value === val);
        if (radio) radio.checked = true;
      });
      this.onChange();
    }
    onChange() {
      const opts = this.getOptions();
      this.querySelectorAll('fieldset.swatch').forEach((f, i) => { const out = f.querySelector('[data-selected-value]'); if (out) out.textContent = opts[i] || ''; });
      this.updateAvailability();
      if (opts.some((o) => !o)) return; // still unselected
      const variant = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      this.current = variant || null;
      this.render(variant);
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant, sectionId: this.sectionId } }));
    }
    updateAvailability() {
      // mark swatches/options that cannot form an available variant with the other current selections
      const opts = this.getOptions();
      const positions = opts.length;
      for (let p = 0; p < positions; p++) {
        const inputs = this.querySelectorAll(`input[data-option-position="${p + 1}"]`);
        const optionsEls = this.querySelectorAll(`select[data-option-position="${p + 1}"] option`);
        const check = (val) => this.variants.some((v) => v.available && v.options[p] === val && v.options.every((o, i) => i === p || !opts[i] || opts[i] === o));
        inputs.forEach((inp) => { const lbl = this.querySelector(`label[for="${inp.id}"]`); if (lbl) lbl.classList.toggle('is-unavailable', !check(inp.value)); });
        optionsEls.forEach((o) => { if (!o.value) return; const ok = check(o.value); o.textContent = o.value + (ok ? '' : ' — ' + str('unavailable', 'Unavailable')); });
      }
    }
    render(variant) {
      const sec = this.section;
      const form = document.getElementById(this.dataset.formId);
      const idInputs = document.querySelectorAll(`#${this.dataset.formId} [data-variant-id], input[name="id"][form="${this.dataset.formId}"]`);
      idInputs.forEach((i) => { i.value = variant ? variant.id : ''; });
      // price
      sec.querySelectorAll('.product__price [data-price-wrapper]').forEach((w) => {
        const priceEl = w.querySelector('[data-price]'), cmpWrap = w.querySelector('.price__compare-wrap'), cmp = w.querySelector('[data-compare-price]'), save = w.querySelector('[data-save-amount]'), from = w.querySelector('.price__from');
        if (from) from.hidden = true;
        if (!variant) return;
        const onSale = variant.compare_at_price > variant.price;
        if (priceEl) priceEl.innerHTML = `<span class="money">${T().money.format(variant.price)}</span>`;
        if (priceEl) priceEl.classList.toggle('price__sale', onSale);
        if (cmpWrap) cmpWrap.hidden = !onSale;
        if (cmp && onSale) cmp.innerHTML = `<span class="money">${T().money.format(variant.compare_at_price)}</span>`;
        if (save) { save.hidden = !onSale; if (onSale) save.innerHTML = `${str('save', 'Save')} <span class="money">${T().money.format(variant.compare_at_price - variant.price)}</span>`; }
        w.classList.toggle('price--on-sale', onSale);
        w.classList.toggle('price--sold-out', !variant.available);
      });
      sec.querySelectorAll('[data-sticky-price]').forEach((p) => { if (variant) p.innerHTML = `<span class="money">${T().money.format(variant.price)}</span>`; });
      // sku
      sec.querySelectorAll('[data-sku]').forEach((el) => { const v = el.querySelector('[data-sku-value]'); el.hidden = !(variant && variant.sku); if (v && variant) v.textContent = variant.sku || ''; });
      // buttons (main + sticky)
      const pf = sec.querySelector('boostly-product-form');
      const zeroText = pf ? pf.dataset.zeroText : '';
      sec.querySelectorAll('.product-form__submit').forEach((btn) => {
        const txt = btn.querySelector('[data-add-text]');
        const isZero = variant && variant.price === 0;
        btn.classList.toggle('product-form__submit--zero', !!isZero);
        if (!variant) { btn.disabled = true; if (txt) txt.textContent = str('unavailable', 'Unavailable'); return; }
        btn.disabled = !variant.available;
        if (txt) txt.textContent = !variant.available ? str('soldOut', 'Sold out') : (isZero && zeroText ? zeroText : str('addToCart', 'Add to cart'));
      });
      // media
      if (variant && variant.featured_media) { const g = sec.querySelector('boostly-gallery'); if (g) g.show(variant.featured_media.id); }
      // url
      if (variant && this.dataset.updateUrl !== 'false') {
        const url = new URL(window.location.href); url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      // inventory for countdown stock mode "inventory"
      const cd = sec.querySelector('boostly-countdown');
      if (cd && variant && variant.inventory_quantity !== undefined && /inventory/.test(cd.dataset.stockMode || '')) {
        cd.dataset.inventory = Math.max(1, variant.inventory_quantity); cd.initStock && cd.initStock();
      }
      if (form) form.dispatchEvent(new Event('variant-updated'));
    }
  }
  define('boostly-variant-picker', BoostlyVariantPicker);

  /* ============================================================== product form */
  class BoostlyProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      // NOTE: never read `this.form.id` — the hidden `<input name="id">` (the variant id field every
      // product/cart form has) shadows HTMLFormElement's `id` IDL property with itself (a well-known
      // forms gotcha: a named form control named "id" wins over the element's own `id` attribute), so
      // `this.form.id` resolves to that <input> element, not the form's id string. Use getAttribute().
      this.formId = this.form.getAttribute('id');
      this.button = this.form.querySelector('[data-add-button]');
      this.error = this.form.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    getQuantityInput() {
      return document.querySelector(`input[name="quantity"][form="${this.formId}"]`) || this.form.querySelector('input[name="quantity"]');
    }
    async onSubmit(e, { skipUpsell = false, extraItems = null } = {}) {
      if (e) e.preventDefault();
      if (!this.button || this.button.disabled && !extraItems) return;
      const idInput = this.form.querySelector('[name="id"]');
      if (!idInput || !idInput.value) { this.showError(str('choose', 'Please choose an option')); return; }
      // quantity upsell (buy N get a gift) — original relate_pop() flow, now working
      const upsell = !skipUpsell && !this.hasAttribute('data-quick-add') && document.querySelector(`boostly-quantity-upsell[data-form-id="${this.formId}"]`);
      const qtyInput = this.getQuantityInput();
      const qty = qtyInput ? Number(qtyInput.value) || 1 : 1;
      if (upsell && qty < Number(upsell.dataset.threshold || 2) && !upsell.shownOnce) {
        upsell.open(this); return;
      }
      this.setLoading(true);
      this.hideError();
      try {
        let result;
        if (extraItems) {
          const fd = new FormData(this.form);
          const props = {}; for (const [k, v] of fd.entries()) { const m = k.match(/^properties\[(.+)\]$/); if (m) props[m[1]] = v; }
          const main = { id: idInput.value, quantity: qty, properties: Object.assign(props, extraItems.mainProperties || {}) };
          result = await T().cart.add([main].concat(extraItems.items));
        } else {
          const fd = new FormData(this.form);
          if (qtyInput && !fd.has('quantity')) fd.append('quantity', qty);
          fd.delete('return_to');
          result = await T().cart.add(fd);
        }
        this.onAdded(result);
      } catch (err) {
        const msg = err.message || str('addError', 'Error');
        this.setLoading(false); // must run before the sold-out branch below, since it unconditionally re-enables the button
        this.showError(msg);
        if (err.status === 422 || /^All /i.test(msg)) {
          // Out of stock / quantity limit reached (original: response starting with "All " -> disable
          // the button and show "Sold out" so the shopper can't keep retrying an impossible add).
          this.button.disabled = true;
          const txt = this.button.querySelector('[data-add-text]');
          if (txt) txt.textContent = str('soldOut', 'Sold out');
        }
      }
    }
    onAdded() {
      const btnText = this.button.querySelector('[data-add-text]');
      const original = btnText ? btnText.textContent : '';
      this.setLoading(false);
      if (btnText) btnText.textContent = str('added', 'Added!');
      this.button.classList.add('is-added');
      setTimeout(() => { if (btnText) btnText.textContent = original; this.button.classList.remove('is-added'); }, 700); // original howLongTillBtnReturnsToNormal
      const qtyInput = this.getQuantityInput(); if (qtyInput) { qtyInput.value = qtyInput.min || 1; qtyInput.dispatchEvent(new Event('change', { bubbles: true })); }
      const after = this.dataset.afterAdd || 'stay';
      // related upsell popup takes over navigation if it will show
      const popup = document.querySelector('boostly-upsell-popup');
      if (!this.hasAttribute('data-quick-add') && popup && popup.canOpen && popup.canOpen()) { popup.open({ afterAdd: after }); return; }
      BoostlyProductForm.afterAdd(after);
    }
    static afterAdd(after) {
      if (after !== 'checkout' && typeof T().afterAddHook === 'function' && T().afterAddHook(after) === true) return;
      if (after === 'checkout') { window.location.href = (T().routes.checkout || '/checkout'); return; }
      if (after === 'cart') { window.location.href = T().routes.cart || '/cart'; return; }
      T().toast.show(`<strong>${str('addedToCart', 'Added to cart')}</strong> — <a href="${T().routes.cart}">${str('viewCart', 'View cart')}</a> · <a href="#" data-toast-dismiss onclick="this.closest('.toast').querySelector('.toast__close').click();return false;">${str('continueShopping', 'Continue shopping')}</a>`);
    }
    setLoading(on) { if (!this.button) return; this.button.classList.toggle('is-loading', on); this.button.setAttribute('aria-busy', on); if (on) this.button.disabled = true; else this.button.disabled = false; }
    showError(msg) { if (this.error) { this.error.textContent = msg; this.error.hidden = false; } else T().toast.show(msg, { type: 'error' }); }
    hideError() { if (this.error) this.error.hidden = true; }
  }
  define('boostly-product-form', BoostlyProductForm);
  window.BoostlyProductForm = BoostlyProductForm;

  /* ================================================================ sticky ATC */
  class BoostlyStickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) return;
      this.mainBtn = this.form.querySelector('[data-add-button]');
      this.hidden = false;
      const desktop = this.classList.contains('sticky-atc--desktop');
      const io = new IntersectionObserver(([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        const allowed = desktop || window.matchMedia('(max-width: 749px)').matches;
        this.classList.toggle('is-visible', past && allowed);
        document.documentElement.style.setProperty('--sticky-atc-height', past && allowed ? this.offsetHeight + 'px' : '0px');
      });
      io.observe(this.mainBtn || this.form);
      this.querySelector('[data-sticky-submit]').addEventListener('click', () => {
        if (this.mainBtn && this.mainBtn.disabled) {
          (this.form.closest('.product') || document.body).scrollIntoView({ behavior: 'smooth' });
          return;
        }
        if (this.form.requestSubmit) this.form.requestSubmit(this.mainBtn); else this.form.dispatchEvent(new Event('submit', { cancelable: true }));
      });
      // quantity sync (both ways). NOTE: use dataset.formId (a plain string), never this.form.id — the
      // form's hidden <input name="id"> (variant id) shadows HTMLFormElement's `id` IDL property with
      // itself, so `this.form.id` would resolve to that <input>, not the form's id string.
      const sticky = this.querySelector('[data-sticky-qty]');
      const main = document.querySelector(`input[name="quantity"][form="${this.dataset.formId}"]`);
      if (sticky && main) {
        sticky.addEventListener('change', () => { main.value = sticky.value; main.dispatchEvent(new Event('change', { bubbles: true })); });
        main.addEventListener('change', () => { if (sticky.value !== main.value) sticky.value = main.value; });
      } else if (sticky && !main) {
        sticky.name = 'quantity'; sticky.setAttribute('form', this.dataset.formId);
      }
      // mirror button state
      const mirror = () => {
        const btn = this.querySelector('[data-sticky-submit]');
        const t1 = this.mainBtn.querySelector('[data-add-text]'), t2 = btn.querySelector('[data-add-text]');
        if (t1 && t2) t2.textContent = t1.textContent;
        btn.classList.toggle('product-form__submit--zero', this.mainBtn.classList.contains('product-form__submit--zero'));
        btn.classList.toggle('is-loading', this.mainBtn.classList.contains('is-loading'));
        btn.disabled = false; // sticky button stays clickable: when main is disabled it scrolls to the options
      };
      const obs = new MutationObserver(mirror);
      if (this.mainBtn) { mirror(); obs.observe(this.mainBtn, { attributes: true, childList: true, subtree: true, characterData: true }); }
    }
  }
  define('boostly-sticky-atc', BoostlyStickyAtc);

  /* =========================================================== visitor counter */
  class BoostlyVisitorCounter extends HTMLElement {
    connectedCallback() {
      this.num = this.querySelector('[data-visitor-number]');
      this.min = Number(this.dataset.min) || 1; this.max = Number(this.dataset.max) || 20;
      this.render(T() ? T().random.int(this.min, this.max) : this.min);
      this._t = setInterval(() => {
        // drift by ±1..3 like a live counter, clamped to the configured range (original: new random each 2s)
        const cur = Number(this.value) || this.min;
        const next = Math.min(this.max, Math.max(this.min, cur + T().random.int(-3, 3)));
        this.render(next);
      }, Number(this.dataset.interval) || 2000);
    }
    disconnectedCallback() { clearInterval(this._t); }
    render(n) { this.value = n; if (this.num) this.num.innerHTML = String(n).split('').map((d) => `<span class="visitor-counter__digit">${d}</span>`).join(''); }
  }
  define('boostly-visitor-counter', BoostlyVisitorCounter);

  /* ================================================================ read more */
  class BoostlyReadMore extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-read-more-content]');
      this.btn = this.querySelector('[data-read-more-toggle]');
      const h = Number(this.dataset.height) || 153;
      requestAnimationFrame(() => {
        if (this.content.scrollHeight > h + 16) { this.classList.add('is-collapsed'); this.btn.hidden = false; } // heightMargin 16
      });
      this.btn.addEventListener('click', () => {
        const open = this.classList.toggle('is-expanded');
        this.classList.toggle('is-collapsed', !open);
        this.btn.setAttribute('aria-expanded', open);
        this.btn.querySelector('[data-more]').hidden = open; this.btn.querySelector('[data-less]').hidden = !open;
        if (!open) this.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    }
  }
  define('boostly-read-more', BoostlyReadMore);

  /* ========================================================== quantity upsell */
  class BoostlyQuantityUpsell extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.querySelector('[data-upsell-yes]').addEventListener('click', () => this.accept());
      this.querySelector('[data-upsell-no]').addEventListener('click', () => this.decline());
      this.dialog.addEventListener('close', () => { if (!this._resolved) this._pending = null; });
    }
    open(productForm) { this._pending = productForm; this._resolved = false; this.shownOnce = true; T().modal.open(this.dialog); }
    accept() {
      const pf = this._pending; this._resolved = true; T().modal.close(this.dialog);
      if (!pf) return;
      const threshold = Number(this.dataset.threshold) || 2;
      const q = pf.getQuantityInput(); if (q) q.value = threshold;
      const items = [];
      if (this.dataset.freeVariant) items.push({ id: this.dataset.freeVariant, quantity: 1, properties: { _free_product: 'true', _free_product_parent_id: this.dataset.productId, _upsell_min_qty: String(threshold) } });
      pf.onSubmit(null, { skipUpsell: true, extraItems: { items, mainProperties: { _upsell_min_qty: String(threshold) } } });
    }
    decline() { const pf = this._pending; this._resolved = true; T().modal.close(this.dialog); if (pf) pf.onSubmit(null, { skipUpsell: true }); }
  }
  define('boostly-quantity-upsell', BoostlyQuantityUpsell);

  /* ================================================================ copy link */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-copy-link]'); if (!b) return;
    const url = b.getAttribute('data-copy-link');
    (navigator.clipboard ? navigator.clipboard.writeText(url) : Promise.reject()).then(() => T().toast.show(str('copied', 'Link copied')), () => window.prompt('', url));
  });
})();
